import { Borrower } from 'app/models/dto/borrower.model';
import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Observable, Subscription, combineLatest, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { Store, Select } from '@ngxs/store';
import { InitiateCall } from 'app/store/call/call.actions';
import { ActiveCall } from 'app/models/dto/call.model';
import { CallState } from 'app/store/call/call.state';
import { notNull } from 'app/modules/shared/functions/filters';
import { BorrowerValueState } from 'app/store/borrower-value/borrower-value.state';
import { BorrowerValue } from 'app/models/dto/borrower-value.model';
import { ContactState } from 'app/store/contact/contact.state';
import { Contact } from 'app/models/dto/contact.model';
import { CountryDataState } from 'ng2-app/store/country/country-data.state';
import { NonOwnerPersonnelState } from 'app/store/non-owner-personnel/non-owner-personnel.state';

@Component({
  selector: 'call-button',
  templateUrl: 'call-button.component.html',
  styleUrls: ['./call-button.component.scss']
})
export class CallButtonComponent implements OnInit, OnDestroy {

  @Input() borrowerId: number | string;
  @Input() flat: boolean;
  @Select(CallState.call) call$: Observable<ActiveCall>;
  @Select(CallState.processing) processingCall$: Observable<boolean>;
  @Select(ContactState.primaryContact) primaryContact$: Observable<Contact>;
  @Select(ContactState.contacts) contacts$: Observable<Contact[]>;
  @Select(NonOwnerPersonnelState.nonOwnerPersonnel) nonOwnerContacts$: Observable<Contact[]>;

  borrower$: Observable<Borrower>;
  phoneCountry: string | null = null;
  phoneCountryCode: number | null = null;
  borrower: Borrower;
  borrowerPhone$: Observable<BorrowerValue | null>;
  borrowerPhoneCountryCodeId$: Observable<BorrowerValue | null>;
  complianceReview$: Observable<BorrowerValue | null>;
  borrowerValuePhone: BorrowerValue | null;
  isCallButtonDisabled$: Observable<boolean>;
  showDropdownButton: boolean;
  oneNumberToRuleThemAll: boolean;
  callSub: Subscription;
  activeCall: any;
  destroyed$ = new Subject<boolean>();
  primaryContact: Contact;
  joinedContacts$: Observable<Contact[]>;
  totalContactCount: number;

  constructor(
    private store: Store,
    private cdRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.borrower$ = this.store.select(state => state.borrower.borrower)
      .pipe(filter(notNull), takeUntil(this.destroyed$));
    this.borrowerPhone$ = this.store.select(BorrowerValueState.valueByAlias)
      .pipe(takeUntil(this.destroyed$), map(filterFn => filterFn('borrowerPhone')));
    this.borrowerPhoneCountryCodeId$ = this.store.select(BorrowerValueState.valueByAlias)
      .pipe(takeUntil(this.destroyed$), map(filterFn => filterFn('borrowerPhoneCountryCodeId')));
    this.borrower$.subscribe(borrower => {
      if (Number(borrower.id) === Number(this.borrowerId)) {
        this.borrower = { ...borrower };
      }
      this.shouldShowDropdown();
    });

    this.borrowerPhone$.pipe(takeUntil(this.destroyed$)).subscribe(phone => {
      this.borrowerValuePhone = phone;
      this.shouldShowDropdown();
    });

    this.borrowerPhoneCountryCodeId$.pipe(takeUntil(this.destroyed$)).subscribe(countryCodeId => {
      // Default the phone country code id to US if one is not set
      const countryCodeIdValue = countryCodeId?.value ? countryCodeId.value : '2';

      this.store.select(CountryDataState.countryFromPhoneCountyCodeId)
        .pipe(takeUntil(this.destroyed$),
          map(filterFn => filterFn(countryCodeIdValue))).subscribe((countryData) => {
            if (countryData) {
              this.phoneCountry = countryData.countryCode || null;
              this.phoneCountryCode = countryData.phone_country_code?.countryCode || null;
            }
          });
    });

    this.primaryContact$.pipe(takeUntil(this.destroyed$)).subscribe(primaryContact => {
      this.primaryContact = primaryContact;
      this.shouldShowDropdown();
    });

    this.joinedContacts$ = combineLatest([this.contacts$, this.nonOwnerContacts$]).pipe(
      map(([contacts, nonOwnerContacts]) => {
        const owners = contacts.filter(
          contact => this.phoneNumberExists(contact.mobilePhone) || this.phoneNumberExists(contact.workPhone)
        );
        const nonOwners = nonOwnerContacts.filter(
          contact => this.phoneNumberExists(contact.mobilePhone) || this.phoneNumberExists(contact.workPhone)
        );
        return owners.concat(nonOwners);
      }),
      tap((results) => {
        this.totalContactCount = results.length;
        this.shouldShowDropdown();
      })
    );

    this.complianceReview$ = this.store.select(BorrowerValueState.valueByAlias)
      .pipe(takeUntil(this.destroyed$), map(filterFn => filterFn('complianceReview')));

    this.isCallButtonDisabled$ = combineLatest([this.call$, this.borrower$, this.complianceReview$, this.processingCall$]).pipe(
      map(([call, borrower, complianceReview, processingCall]) => (
        processingCall
        || (call && call.inProgress)
        || !borrower
        || borrower.status === 'dead'
        || borrower.status === 'doNotContact'
        || (this.getAvailableNumberCount() === 0 && this.totalContactCount === 0)
        || complianceReview?.value !== null
      ))
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next(false);
  }

  shouldShowDropdown() {
    this.showDropdownButton = 
      (this.getAvailableNumberCount() > 1 || this.totalContactCount >= 1);

    this.oneNumberToRuleThemAll = 
      (this.getAvailableNumberCount() === 1 && this.totalContactCount === 0);

    this.cdRef.markForCheck();
  }

  /**
   * Return the count of available phone numbers for the given borrowers.
   */
  getAvailableNumberCount() {
    if (!this.borrower) {
      return 0;
    }
    let count = 0;
    if (this.borrower.phone && this.phoneNumberExists(this.borrower.phone)) {
      count++;
    }
    if (this.borrowerValuePhone && this.phoneNumberExists(this.borrowerValuePhone.value)) {
      count++;
    }
    if (this.primaryContact && this.phoneNumberExists(this.primaryContact.workPhone)) {
      count++;
    }
    if (this.primaryContact && this.phoneNumberExists(this.primaryContact.mobilePhone)) {
      count++;
    }
    return count;
  }

  /**
   * Returns the first available phone number from the borrower in the following order
   * 1. Main phone 2. Work phone 3. Mobile phone 4. null
   */
  get firstAvailablePhone() {
    if (this.borrowerValuePhone && this.phoneNumberExists(this.borrowerValuePhone.value)) {
      const phoneCountryCode = this.phoneCountryCode ? this.phoneCountryCode.toString() : '1';
      return `${phoneCountryCode}${this.borrowerValuePhone.value}`;
    } else if (this.primaryContact && this.phoneNumberExists(this.primaryContact.workPhone)) {
      return `${this.primaryContact.work_phone_country_code.countryCode.toString()}${this.primaryContact.workPhone.toString()}`;
    } else if (this.primaryContact && this.phoneNumberExists(this.primaryContact.mobilePhone)) {
      return `${this.primaryContact.mobile_phone_country_code.countryCode.toString()}${this.primaryContact.mobilePhone.toString()}`;
    } else if (this.borrower.phone && this.phoneNumberExists(this.borrower.phone)) {
      const phoneCountryCode = this.phoneCountryCode ? this.phoneCountryCode.toString() : '1';
      return `${phoneCountryCode}${this.borrower.phone}`;
    }

    return null;
  }

  phoneNumberExists(number: string | null) {
    return number !== undefined && number !== null && number.trim() !== '';
  }

  callBorrower(phoneNumber: number | string, countryCode: string | null) {
    if (countryCode) {
      if (`${phoneNumber}`.length > 10) {
        phoneNumber = `${phoneNumber}`.includes('+') ? `${phoneNumber}` : `+${phoneNumber}`;
      } else {
        phoneNumber = `+${countryCode}${phoneNumber}`;
      }
    } else {
      phoneNumber = `${phoneNumber}`;
    }

    this.store.dispatch(new InitiateCall({ borrowerId: this.borrower.id, phone: phoneNumber, borrower: this.borrower }));
  }
}
